import { gql } from "@apollo/client";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { defaultCalendarBlockTitles } from "@caredna-marlin/utils/calendarBlockHelper";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { DatePickerSlotProps, TimeField } from "@mui/x-date-pickers";
import { endOfDay, formatISO, setHours, startOfDay, startOfHour, startOfTomorrow, startOfYesterday } from "date-fns";
import { groupBy, sortBy, startCase } from "lodash";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "~/components/common/forms/DatePicker";
import Help from "~/components/common/Help";
import { CopyDemoDayMutationVariables, useCommandIndexCombinedDataQuery, useCopyDemoDayMutation } from "~/graphql/__generated__/graphql";
import { Permission, usePermissionOrRedirect } from "~/hooks/permissions";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { useOrganization } from "~/providers/GraphqlProvider";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { CommandCenterLocationState } from "~/components/command/DateSelector";
import { demoVisitStatus } from "@caredna-marlin/utils/demoHelper";

const slotProps: DatePickerSlotProps<Date, false> = {
  textField: {
    size: "small",
    sx: { backgroundColor: "white", width: 180 },
  },
}

const DemoDataScreen = () => {
  usePermissionOrRedirect(Permission.copyDemoData, "/command");
  const { organizationName } = useOrganization();
  const { visitTypes, errandTypeByRequirementId } = useOrgConfig();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false)

  const [dates, setDates] = useState<{ from: Date, to: Date }>({ from: startOfYesterday(), to: startOfTomorrow()});
  const [demoTime, setDemoTime] = useState<Date>(startOfHour(setHours(new Date(), 11)));

  const { getTimeFormat, datef } = useZonedDateFormat();
  const timeFormatString = getTimeFormat(true, false);

  const { data, loading } = useCommandIndexCombinedDataQuery({
    variables: {
      filterStartTime: formatISO(startOfDay(dates.from)),
      filterEndTime: formatISO(endOfDay(dates.from)),
    }
  });

  const resources = useMemo(() => {
    const timeOnDay = new Date(dates.from);
    timeOnDay.setHours(demoTime.getHours(), demoTime.getMinutes());

    const drives = data?.visitResource.reduce((acc, vr) => {
      acc[vr.visitId] = Math.max(vr.startOfShiftDriveTime, vr.interVisitDriveTime, acc[vr.visitId] || 0);
      return acc;
    }, {} as Record<number, number>) || {};

    const vrs = data?.visitResource.map(vr => {
      const visitEventStatus = vr.visit ? demoVisitStatus({
        startTime: new Date(vr.visit.startTime),
        endTime: new Date(vr.visit.endTime),
        eventStatus: String(vr.visit.eventStatus),
        driveTime: drives[vr.visitId],
      }, timeOnDay) : undefined;

      return {
        ...vr,
        errandStatus: vr.errandStatus
          ? (visitEventStatus === "canceled"
            ? "canceled"
            : demoVisitStatus({
              startTime: new Date(vr.startTime),
              endTime: new Date(vr.endTime),
              eventStatus: String(vr.errandStatus),
              driveTime: Math.max(vr.startOfShiftDriveTime, vr.interVisitDriveTime),
            }, timeOnDay))
          : vr.errandStatus,
        visit: vr.visit ? {
          ...vr.visit,
          eventStatus: visitEventStatus,
        } : null,
      };
    });

    const sortedVRByVisit = Object.values(groupBy(sortBy(vrs, vr => new Date(vr.startTime)), "visit.id"));
    return sortBy(sortedVRByVisit, vrs => new Date(vrs[0].startTime).getTime())
  }, [data, dates, demoTime]);

  const [copyDemoDay, { loading: copyLoading, data: copyResult }] = useCopyDemoDayMutation()
  const onSave = async () => {
    const timeOnDay = new Date(dates.to);
    timeOnDay.setHours(demoTime.getHours(), demoTime.getMinutes());

    const variables: CopyDemoDayMutationVariables = {
      copyFrom: formatISO(startOfDay(dates.from)),
      copyTo: formatISO(startOfDay(dates.to)),
      demoTime: formatISO(timeOnDay),
      doNotCommit: false,
    };

    await copyDemoDay({
      variables,
      awaitRefetchQueries: true,
      refetchQueries: ["CommandIndexCombinedData"],
    });
    setDialogOpen(true)
  }

  return <Box sx={{ height: "100%", width: "100%", overflow: "hidden", mt: 4, mb: 4, ml: 2 }}>
    <Typography variant="h6" sx={{ mb: 2 }}>
      Copy Demo Data - {organizationName}
    </Typography>
    <Paper sx={{ width: "100%", p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box display="flex" gap={2}>
        <DatePicker
          label="Copy from"
          value={dates.from}
          onChange={(from) => from && setDates({ ...dates, from })}
          slots={{ textField: TextField }}
          slotProps={slotProps}
        />
        <DatePicker
          label="Onto"
          value={dates.to}
          onChange={(to) => to && setDates({ ...dates, to })}
          slots={{ textField: TextField }}
          slotProps={slotProps}
        />
        <TimeField
          label="Demo time"
          format={timeFormatString}
          value={demoTime}
          onChange={(newValue) => {
            if (newValue && !Number.isNaN(newValue.getTime())) {
              return setDemoTime(newValue);
            }
          }}
          size="small"
        />
        <Button
          disabled={loading || dates.from.getTime() === dates.to.getTime()}
          loading={copyLoading}
          variant="contained"
          onClick={onSave}
        >Copy visits and blocks</Button>
      </Box>
      <Table>
        <TableHead><TableRow>
          <TableCell>Time</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>
            Status
            <Help text="Visits and errands before the demo time will be marked completed; those after it will be marked Scheduled." />
          </TableCell>
          <TableCell>Provider</TableCell>
          <TableCell>Patient</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {loading && <TableRow>
            <TableCell colSpan={4} sx={{ fontWeight: 700 }}>Loading...</TableCell>
          </TableRow>}
          {Object.values(resources).flatMap(visitResources => visitResources.map((vr, i) => <TableRow
            key={vr.id}
            sx={i > 0 ? {
              td: { pl: 2 },
              "td:before": { content: "'∟ '", pr: 0.5 },
            } : undefined}
          >
            <TableCell>{datef(vr.startTime, timeFormatString)}-{datef(vr.endTime, timeFormatString)}</TableCell>
            <TableCell>{errandTypeByRequirementId[vr.visitResourceRequirementId]?.name ?? visitTypes[vr.visit?.visitTypeId ?? '']?.name}</TableCell>
            <TableCell>{startCase(vr.errandStatus ?? vr.visit?.eventStatus)}</TableCell>
            <TableCell>{vr.provider?.firstName} {vr.provider?.lastName}</TableCell>
            <TableCell>{vr.visit?.patient?.firstName} {vr.visit?.patient?.lastName}</TableCell>
          </TableRow>))}
          {sortBy(data?.calendarBlock, cb => new Date(cb.startTime).getTime()).map(cb => <TableRow key={cb.id}>
            <TableCell>{datef(cb.startTime, timeFormatString)}-{datef(cb.endTime, timeFormatString)}</TableCell>
            <TableCell>{defaultCalendarBlockTitles[cb.blockType]}</TableCell>
            <TableCell></TableCell>
            <TableCell>{cb.provider?.firstName} {cb.provider?.lastName}</TableCell>
            <TableCell></TableCell>
          </TableRow>)}
        </TableBody>
      </Table>
    </Paper>

    {copyResult && <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
      <DialogTitle>Copied from {datef(dates.from, "MMM d")} to {datef(dates.to, "MMM d")}</DialogTitle>
      <DialogContent>
        <ul style={{ margin: 0 }}>
          <li>Cleared out the destination day</li>
          <li>Copied {copyResult.copyDemoDay.visits} Visits</li>
          <ul>
            <li>Copied {copyResult.copyDemoDay.visitResources} Visit Resources</li>
            <li>Copied {copyResult.copyDemoDay.visitNotes} Visit Notes</li>
            <li>Created {copyResult.copyDemoDay.visitEventLogEntries} Visit Event Log Entries</li>
          </ul>
          <li>Copied {copyResult.copyDemoDay.calendarBlocks} Calendar Blocks</li>
        </ul>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => setDialogOpen(false)}>Close</Button>
        <Button
          variant="contained"
          onClick={() => navigate("/command", { state: { date: dates.to } as CommandCenterLocationState})}
          endIcon={<KeyboardDoubleArrowRightIcon
        />}>Command Center</Button>
      </DialogActions>
    </Dialog>}
  </Box>
}

export default DemoDataScreen

gql`
  mutation CopyDemoDay(
    $copyFrom: String!
    $copyTo: String!
    $demoTime: String!
    $doNotCommit: Boolean
  )
  @permission(requires: copyDemoData)
  {
    copyDemoDay(
      copyFrom: $copyFrom
      copyTo: $copyTo
      demoTime: $demoTime
      doNotCommit: $doNotCommit
    ) {
      calendarBlocks
      deletedCount
      visits
      visitEventLogEntries
      visitNotes
      visitResources
      visitServices
    }
  }
`
